import { USER_TYPE } from "../system.js";
import { CORE_PERMISSIONS } from "./corePermissionKeys.js";
import { PERMISSION_GROUPS } from "./permissionGroups.js";
export const PERMISSION_METADATA = {
    // Dashboard permissions
    [CORE_PERMISSIONS.VIEW_BOOKING_DASHBOARD]: {
        group: PERMISSION_GROUPS.DASHBOARDS,
        entityKeys: ['bookingEntityName', 'userEntityName'],
        nameTemplate: 'View {bookingEntityName} Dashboard',
        descriptionTemplate: 'Allows {userEntityName} to view and monitor the {bookingEntityName} metrics'
    },
    [CORE_PERMISSIONS.VIEW_SURVEY_DASHBOARD]: {
        group: PERMISSION_GROUPS.DASHBOARDS,
        entityKeys: ['surveyEntityName', 'userEntityName'],
        nameTemplate: 'View {surveyEntityName} Dashboard',
        descriptionTemplate: 'Allows {userEntityName} to view and monitor the {surveyEntityName} metrics'
    },
    // User management permissions
    [CORE_PERMISSIONS.MANAGE_ADMINISTRATORS]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ['userEntityName'],
        nameTemplate: 'Manage Administrator',
        descriptionTemplate: 'Allows {userEntityName} to manage Administrator users'
    },
    [CORE_PERMISSIONS.MANAGE_MANAGERS]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ['managersEntityName', 'managerEntityName', 'userEntityName'],
        nameTemplate: 'Manage {managersEntityName}',
        descriptionTemplate: 'Allows {userEntityName} to manage the {managerEntityName} users'
    },
    [CORE_PERMISSIONS.MANAGE_ACCESS_ROLES]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ['managerEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage Access Roles',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the {managerEntityName}/{adminsEntityName} Access Roles'
    },
    [CORE_PERMISSIONS.MANAGE_RECIPIENTS]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Manage Recipients',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the Recipients'
    },
    // Respondent permissions
    [CORE_PERMISSIONS.MANAGE_RESPONDENTS]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ['respondentsEntityName', 'respondentEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage {respondentsEntityName}',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the {respondentEntityName} users'
    },
    [CORE_PERMISSIONS.RESPONDENT_LIST]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ['respondentsEntityName', 'respondentEntityName', 'userEntityName'],
        nameTemplate: 'View List of {respondentsEntityName}',
        descriptionTemplate: 'Allows {userEntityName} to view the list of assigned {respondentEntityName}s'
    },
    [CORE_PERMISSIONS.RESPONDENT_READ]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ['respondentEntityName', 'userEntityName'],
        nameTemplate: 'View Profile',
        descriptionTemplate: 'Allows {userEntityName} to view the {respondentEntityName}\'s profile including profile details'
    },
    [CORE_PERMISSIONS.RESPONDENT_WRITE]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ['respondentEntityName', 'respondentsEntityName', 'userEntityName', 'surveyTypeEntityName'],
        nameTemplate: 'Create and Edit {respondentEntityName}',
        descriptionTemplate: 'Allows {userEntityName} to create and edit {respondentsEntityName} details including email and phone. Allows setting of companies and {surveyTypeEntityName} to the {respondentEntityName}'
    },
    [CORE_PERMISSIONS.RESPONDENT_DOCUMENT_READ]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ['respondentEntityName', 'userEntityName'],
        nameTemplate: 'View Document',
        descriptionTemplate: 'Allows {userEntityName} to view documents that were uploaded for this {respondentEntityName}'
    },
    [CORE_PERMISSIONS.RESPONDENT_DOCUMENT_WRITE]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ['respondentEntityName', 'userEntityName'],
        nameTemplate: 'Upload Document',
        descriptionTemplate: 'Allows {userEntityName} to upload documents for this {respondentEntityName}\'s profile'
    },
    [CORE_PERMISSIONS.RESPONDENT_NOTE_READ]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ['respondentEntityName', 'userEntityName'],
        nameTemplate: 'View Notes',
        descriptionTemplate: 'Allows {userEntityName} to view notes created for this {respondentEntityName}'
    },
    [CORE_PERMISSIONS.RESPONDENT_NOTE_WRITE]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ['respondentEntityName', 'userEntityName'],
        nameTemplate: 'Create and Delete Notes',
        descriptionTemplate: 'Allows {userEntityName} to create and delete notes for this {respondentEntityName}'
    },
    [CORE_PERMISSIONS.MANAGE_RESPONDENT_COMPANIES]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ['respondentEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage {respondentEntityName}\'s Company',
        descriptionTemplate: 'Allows {adminsEntityName} to assign a company to the {respondentEntityName}',
        subPermission: true
    },
    // Booking permissions
    [CORE_PERMISSIONS.MANAGE_BOOKINGS]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingsEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage {bookingsEntityName}',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the {bookingsEntityName}'
    },
    [CORE_PERMISSIONS.BOOKING_READ]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingsEntityName', 'extUserEntityName', 'respondentEntityName', 'managerEntityName', 'managersEntityName'],
        nameTemplate: 'View {bookingsEntityName}',
        descriptionByUserType: {
            [USER_TYPE.MANAGER]: '<p>Allows {managerEntityName} to view:</p><ul><li>{bookingsEntityName} made to any {managerEntityName} at your location and</li><li>{bookingsEntityName} specifically assigned to you as {managerEntityName} at your location</li></ul><p>It does not allows to view {bookingsEntityName} specifically assigned to other {managersEntityName} at your location.</p>',
            [USER_TYPE.EXTERNAL_USER]: '<p>Allows {extUserEntityName} to view {bookingsEntityName.toLowerCase()} as long as:</p><ul><li>{extUserEntityName} is assigned to the {respondentEntityName} or</li><li>{respondentEntityName} currently belongs to a company that is associated to the {extUserEntityName}</li></ul>'
        }
    },
    [CORE_PERMISSIONS.BOOKING_READ_INCLUDE_ALL_RESPONDENT_BOOKINGS]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingsEntityName', 'bookingEntityName', 'respondentEntityName', 'extUserEntityName', 'managerEntityName'],
        nameTemplate: 'View All {bookingsEntityName}',
        descriptionByUserType: {
            [USER_TYPE.MANAGER]: '<p>Allows {managerEntityName} to view:</p><ul><li>Every {bookingEntityName} made at your location</li></ul>',
            [USER_TYPE.EXTERNAL_USER]: '<p>Allows {managerEntityName} to view:</p><ul><li>Every {bookingEntityName} belonging to {respondentEntityName} assigned to the {extUserEntityName}</li></ul>'
        },
        subPermission: true
    },
    [CORE_PERMISSIONS.BOOKING_READ_INCLUDE_AFFILIATE_BOOKINGS]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingsEntityName', 'bookingEntityName', 'managerEntityName'],
        nameTemplate: 'View Affiliate {bookingsEntityName}',
        descriptionTemplate: 'Allows {managerEntityName} to view affiliate {bookingEntityName}s',
        subPermission: true
    },
    [CORE_PERMISSIONS.BOOKING_WRITE]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingEntityName', 'userEntityName'],
        nameTemplate: 'Edit {bookingEntityName} Details',
        descriptionTemplate: 'Allows {userEntityName} to change the status and details (i.e. date and time, service and location) of a {bookingEntityName}.'
    },
    [CORE_PERMISSIONS.BOOKING_CREATE_VIA_BOOKING_PAGE]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingsEntityName', 'bookingEntityName', 'bookingPageEntityName', 'bookingPagesEntityName'],
        nameTemplate: 'Create {bookingsEntityName} via {bookingPageEntityName}',
        descriptionTemplate: 'Allows making a {bookingEntityName} from {bookingPagesEntityName}.'
    },
    [CORE_PERMISSIONS.BOOKING_CREATE_VIA_CALENDAR]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingsEntityName', 'userEntityName', 'managerEntityName'],
        nameTemplate: 'Create {bookingsEntityName} via Calendar',
        descriptionTemplate: 'Allows making a {bookingsEntityName} through Calendar. {userEntityName} that has this permission will be able to book at any location with any {managerEntityName.toLowerCase()}.'
    },
    [CORE_PERMISSIONS.EVENT_CREATE_VIA_CALENDAR]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        nameTemplate: 'Create/Update Event via Calendar',
        descriptionTemplate: 'Allows making and updating an Event through Calendar.'
    },
    [CORE_PERMISSIONS.BOOKING_DOCUMENT_READ]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingEntityName', 'userEntityName'],
        nameTemplate: 'View {bookingEntityName} Document',
        descriptionTemplate: 'Allows {userEntityName} to see {bookingEntityName} documents added to a {bookingEntityName}'
    },
    [CORE_PERMISSIONS.BOOKING_DOCUMENT_WRITE]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingEntityName', 'userEntityName'],
        nameTemplate: 'Upload {bookingEntityName} Document',
        descriptionTemplate: 'Allows {userEntityName} to upload a {bookingEntityName} document for a {bookingEntityName}'
    },
    [CORE_PERMISSIONS.BOOKING_CALENDAR_READ]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingsEntityName', 'bookingEntityName', 'userEntityName'],
        nameTemplate: 'View {bookingsEntityName} via {bookingEntityName} Calendar',
        descriptionTemplate: 'Allows {userEntityName} to view {bookingsEntityName} via the Calendar'
    },
    [CORE_PERMISSIONS.MANAGE_BOOKING_COMPANIES]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage {bookingEntityName}\'s Company',
        descriptionTemplate: 'Allows {adminsEntityName} to assign a company to the {bookingEntityName}',
        subPermission: true
    },
    [CORE_PERMISSIONS.MANAGE_CONFIRMATION_TEXT]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage Confirmation Text',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the {bookingEntityName} Confirmation Text'
    },
    [CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingPagesEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage Services, Locations and {bookingPagesEntityName}',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the Services, Locations and {bookingPagesEntityName}'
    },
    [CORE_PERMISSIONS.MANAGE_BOOKING_PAGE_COMPANIES]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ['bookingPageEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage Booking Page\'s Company',
        descriptionTemplate: 'Allows {adminsEntityName} to assign a company to the {bookingPageEntityName}',
        subPermission: true
    },
    // Survey permissions
    [CORE_PERMISSIONS.MANAGE_SURVEY_TYPES]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ['surveyTypesEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage {surveyTypesEntityName}',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the {surveyTypesEntityName}'
    },
    [CORE_PERMISSIONS.MANAGE_SURVEY_BACKGROUNDS]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ['surveyEntityName', 'adminsEntityName'],
        nameTemplate: 'Manage {surveyEntityName} Backgrounds',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the {surveyEntityName} Backgrounds'
    },
    [CORE_PERMISSIONS.SURVEY_READ]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ['surveyEntityName', 'surveysEntityName', 'respondentEntityName', 'userEntityName'],
        nameTemplate: 'View {surveyEntityName}',
        descriptionTemplate: 'Allows {userEntityName} to view {surveysEntityName} created for this {respondentEntityName}'
    },
    [CORE_PERMISSIONS.SURVEY_WRITE]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ['surveyEntityName', 'surveysEntityName', 'respondentEntityName', 'userEntityName'],
        nameTemplate: 'Create and Answer {surveyEntityName}',
        descriptionTemplate: 'Allows {userEntityName} to create {surveysEntityName} and answer them for this {respondentEntityName}'
    },
    [CORE_PERMISSIONS.VIEW_REPORTS]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ['surveyEntityName', 'adminsEntityName'],
        nameTemplate: 'View Reports',
        descriptionTemplate: 'Allows {adminsEntityName} to view the {surveyEntityName} Reports'
    },
    [CORE_PERMISSIONS.VIEW_FLAGGED_QUESTIONS]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ['surveysEntityName', 'userEntityName'],
        nameTemplate: 'View Flagged Questions',
        descriptionTemplate: 'Allows {userEntityName} to view flagged questions in {surveysEntityName}, and create a link for reviewing flagged question.',
    },
    [CORE_PERMISSIONS.SURVEY_COMPLETED_SEND]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ['surveyEntityName', 'surveysEntityName'],
        nameTemplate: 'Send Completed {surveyEntityName}',
        descriptionTemplate: 'Allow sending completed {surveysEntityName}'
    },
    // Sales and Invoicing permissions
    [CORE_PERMISSIONS.CONFIGURE_SALES_AND_INVOICING]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Configure Sales and Invoicing',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to manage all sales and invoicing settings including payment configurations, tax rates, items catalog, and billing configurations'
    },
    [CORE_PERMISSIONS.MANAGE_SALES_INVOICE_CONFIG]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Manage Payment Global Configuration',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to configure global sales and invoicing settings',
        subPermission: true
    },
    [CORE_PERMISSIONS.MANAGE_TAX_RATES]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Manage Tax Rates',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to create, edit and delete tax rates used in invoices and payments',
        subPermission: true
    },
    [CORE_PERMISSIONS.MANAGE_ITEMS]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Manage Items',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to create, edit and delete items',
        subPermission: true
    },
    [CORE_PERMISSIONS.MANAGE_SERVICE_SURVEY_ITEMS_CONFIG]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName', 'surveyTypeEntityName'],
        nameTemplate: 'Manage Service/{surveyTypeEntityName} Billing Items',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to configure billing items in service and {surveyTypeEntityName.toLowerCase()}',
        subPermission: true
    },
    [CORE_PERMISSIONS.MANAGE_BOOKING_PAGE_PAYMENT_CONFIG]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName', 'bookingPageEntityName'],
        nameTemplate: 'Manage Booking Page Payment Configuration',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to configure payment settings for individual {bookingPageEntityName.toLowerCase()}s',
        subPermission: true
    },
    [CORE_PERMISSIONS.INVOICES_AND_PAYMENTS]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Manage Invoices and Payments',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to manage all invoices and payment transactions'
    },
    [CORE_PERMISSIONS.VIEW_ALL_INVOICES]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'View All Invoices',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to view all invoices in the system',
        subPermission: true
    },
    [CORE_PERMISSIONS.CREATE_INVOICES]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Create Invoices',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to create new invoices',
        subPermission: true
    },
    [CORE_PERMISSIONS.EDIT_INVOICES]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Edit Invoices',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to edit existing invoices',
        subPermission: true
    },
    [CORE_PERMISSIONS.CHANGE_INVOICE_PAYMENT_STATUS]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Change Invoice Payment Status',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to change the invoice and payment status',
        subPermission: true
    },
    [CORE_PERMISSIONS.EXPORT_SEND_INVOICES]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Export and Send Invoices',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to export invoices and send them to customers',
        subPermission: true
    },
    [CORE_PERMISSIONS.MANAGE_ADD_ON_CHARGES]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Manage Add-on Charges',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to create and manage add-on charges for bookings',
        subPermission: true
    },
    [CORE_PERMISSIONS.VIEW_PAYMENT_TRANSACTION]: {
        group: PERMISSION_GROUPS.SALES,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'View Payment Transactions',
        descriptionTemplate: 'Allows {adminsEntityName.toLowerCase()} to view all payment transactions in the system',
        subPermission: true
    },
    // Other permissions
    [CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS]: {
        group: PERMISSION_GROUPS.OTHERS,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Manage App Configurations and Integrations',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the App Configurations and Integrations'
    },
    [CORE_PERMISSIONS.MANAGE_API]: {
        group: PERMISSION_GROUPS.OTHERS,
        nameTemplate: 'Manage API',
        descriptionTemplate: 'Allows you to manage the API with which clients can read and write to your AtlasOne instance.'
    },
    [CORE_PERMISSIONS.MANAGE_BILLING]: {
        group: PERMISSION_GROUPS.OTHERS,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Manage Billing',
        descriptionTemplate: 'Allows {adminsEntityName} to manage the Billing'
    },
    [CORE_PERMISSIONS.MANAGE_COMPANIES]: {
        group: PERMISSION_GROUPS.OTHERS,
        entityKeys: ['adminsEntityName'],
        nameTemplate: 'Manage Companies',
        descriptionTemplate: 'Allows {adminsEntityName} to create, update, and archive companies, except for those synced from third-party integrations (e.g. iinsight)'
    },
};
//# sourceMappingURL=permissionMetadata.js.map